import { ExtrudeCutDefinitionSnapshotBuilder } from '../../runtime/ExtrudeCutDefinitionSnapshotBuilder';
import type { ExtrudeFeatureEditDefinition } from './ExtrudeFeatureEditDefinition';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/extrude 层；
 * 2. 负责把 cut-extrude 的 UI 定义、执行定义与当前迁移阶段的内部方向语义对齐；
 * 3. 该协调器只处理 cut-extrude，不污染 boss-extrude 的定义链。
 *
 * 文件职责：
 * 1. 创建 cut-extrude 的默认扩展定义；
 * 2. 统一同步 `flipSideToCut`、Direction 1/2 与 cutOptions 的镜像字段；
 * 3. 把默认预览种子安全写回编辑定义，避免宿主层散落赋值逻辑。
 */

const MIN_CUT_EXTRUDE_DEPTH = 1e-6;

/**
 * 拉伸切除预览种子。
 */
export interface CutExtrudePreviewSeed {
    /** Direction 1 默认深度。 */
    direction1Depth: number;
    /** 当前是否翻转切除侧。 */
    flipSideToCut: boolean;
    /** 命中的目标体 ID。 */
    targetBodyId?: string | null;
    /** 目标体沿切除轴厚度。 */
    targetBodyThickness?: number | null;
    /** 当前轮廓对应的完整宿主描述。 */
    hostedContourInput?: ExtrudeCutHostedContourInput;
}

/**
 * 拉伸切除定义协调器。
 */
export class CutExtrudeDefinitionCoordinator {
    /**
     * 创建默认的切除扩展定义。
     *
     * @param input 构造输入。
     * @returns 默认 cutOptions。
     */
    public static createDefaultCutOptions(input: {
        direction1?: ExtrudeDirectionDefinition;
        direction2?: ExtrudeDirectionDefinition;
        existing?: ExtrudeCutDefinition | null;
    } = {}): ExtrudeCutDefinition {
        const direction1 = input.direction1;
        const direction2 = input.direction2;
        const existing = ExtrudeCutDefinitionSnapshotBuilder.clone(input.existing);
        const flipSideToCut = existing?.flipSideToCut === true;

        return {
            minimalMode: existing?.minimalMode ?? true,
            flipSideToCut,
            start: existing?.start ?? {
                type: 'sketch_plane',
            },
            direction1: {
                endCondition: direction1?.endCondition ?? existing?.direction1.endCondition ?? 'blind',
                distance: Math.max(
                    MIN_CUT_EXTRUDE_DEPTH,
                    Math.abs(direction1?.depth ?? existing?.direction1.distance ?? 10),
                ),
                /**
                 * 说明：
                 * 1. 当前阶段 cut-extrude 仍复用统一拉伸工具体方向链；
                 * 2. `flipSideToCut` 需要投影为 tool 侧别，才能驱动预览与正式几何一致；
                 * 3. 后续如果引入开轮廓/普通切除的更完整语义，这里仍是唯一扩展点。
                 */
                reverseDirection: flipSideToCut,
            },
            direction2Enabled: direction2?.enabled ?? existing?.direction2Enabled ?? false,
            direction2: (direction2?.enabled ?? existing?.direction2Enabled ?? false)
                ? {
                    endCondition: direction2?.endCondition ?? existing?.direction2?.endCondition ?? 'blind',
                    distance: Math.max(
                        MIN_CUT_EXTRUDE_DEPTH,
                        Math.abs(direction2?.depth ?? existing?.direction2?.distance ?? 10),
                    ),
                    reverseDirection: false,
                }
                : undefined,
            featureScope: existing?.featureScope,
            bodiesToKeep: existing?.bodiesToKeep,
            hostedContourInput: existing?.hostedContourInput,
        };
    }

    /**
     * 同步 cut-extrude 定义中的镜像字段。
     *
     * @param definition 原始编辑定义。
     * @returns 同步后的编辑定义。
     */
    public static synchronizeDefinition(
        definition: ExtrudeFeatureEditDefinition,
    ): ExtrudeFeatureEditDefinition {
        if (definition.featureKind !== 'cut-extrude' && definition.operation !== 'cut') {
            return definition;
        }

        const flipSideToCut = definition.cutOptions?.flipSideToCut ?? definition.direction1.reverse;
        const direction1 = {
            ...definition.direction1,
            reverse: flipSideToCut,
            draft: {
                ...definition.direction1.draft,
            },
        };
        const direction2 = {
            ...definition.direction2,
            reverse: false,
            draft: {
                ...definition.direction2.draft,
            },
        };

        return {
            ...definition,
            featureKind: 'cut-extrude',
            operation: 'cut',
            direction1,
            direction2,
            thinFeature: {
                ...definition.thinFeature,
            },
            mergeResult: false,
            cutOptions: this.createDefaultCutOptions({
                direction1,
                direction2,
                existing: definition.cutOptions,
            }),
        };
    }

    /**
     * 将默认预览种子写回 cut-extrude 编辑定义。
     *
     * @param definition 当前编辑定义。
     * @param seed 预览种子。
     * @returns 写回后的编辑定义。
     */
    public static applyPreviewSeed(
        definition: ExtrudeFeatureEditDefinition,
        seed: CutExtrudePreviewSeed,
    ): ExtrudeFeatureEditDefinition {
        const synchronizedDefinition = this.synchronizeDefinition(definition);
        return this.synchronizeDefinition({
            ...synchronizedDefinition,
            direction1: {
                ...synchronizedDefinition.direction1,
                depth: Math.max(MIN_CUT_EXTRUDE_DEPTH, Math.abs(seed.direction1Depth)),
                reverse: seed.flipSideToCut,
                draft: {
                    ...synchronizedDefinition.direction1.draft,
                },
            },
            cutOptions: {
                ...this.createDefaultCutOptions({
                    direction1: synchronizedDefinition.direction1,
                    direction2: synchronizedDefinition.direction2,
                    existing: synchronizedDefinition.cutOptions,
                }),
                flipSideToCut: seed.flipSideToCut,
                hostedContourInput: seed.hostedContourInput
                    ? {
                        ...seed.hostedContourInput,
                        ...synchronizedDefinition.cutOptions?.hostedContourInput,
                        targetBodyId: seed.targetBodyId
                            ?? synchronizedDefinition.cutOptions?.hostedContourInput?.targetBodyId
                            ?? seed.hostedContourInput.targetBodyId
                            ?? null,
                        supportFaceObjectId: synchronizedDefinition.cutOptions?.hostedContourInput?.supportFaceObjectId
                            ?? seed.hostedContourInput.supportFaceObjectId
                            ?? null,
                        supportFaceFaceIndex: synchronizedDefinition.cutOptions?.hostedContourInput?.supportFaceFaceIndex
                            ?? seed.hostedContourInput.supportFaceFaceIndex
                            ?? null,
                    }
                    : synchronizedDefinition.cutOptions?.hostedContourInput,
            },
        });
    }
}
